/**
 * Audio cho Luyện giao tiếp (Gemini Live, plan 2026-10-03).
 *
 * - `MicStreamer`: mic → AudioWorklet → PCM16 16kHz (gửi Live theo khối ~100ms) +
 *   giữ toàn bộ PCM của SV (mic chạy liên tục → chỉ số mẫu = thời gian thực).
 * - `AiPlayer`: phát PCM16 24kHz của AI liền mạch, dừng ngay khi bị ngắt lời; ghi
 *   lại từng đoạn AI đã phát kèm mốc thời gian (đã đổi về 16kHz).
 * - `mixToWav()`: trộn 2 luồng theo thời gian → 1 file WAV 16kHz mono (gửi chấm).
 */

export const SAMPLE_RATE = 16000;
const AI_RATE = 24000;

const WORKLET_SRC = `
class PcmCapture extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) this.port.postMessage(ch.slice(0));
    return true;
  }
}
registerProcessor("pcm-capture", PcmCapture);
`;

/** Resample tuyến tính (đủ cho giọng nói). */
export function resample(input: Float32Array, from: number, to: number): Float32Array {
  if (from === to) return input;
  const ratio = from / to;
  const out = new Float32Array(Math.floor(input.length / ratio));
  for (let i = 0; i < out.length; i++) {
    const pos = i * ratio;
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const frac = pos - i0;
    out[i] = input[i0] * (1 - frac) + input[i1] * frac;
  }
  return out;
}

function floatToPcm16(f: Float32Array): Int16Array {
  const out = new Int16Array(f.length);
  for (let i = 0; i < f.length; i++) {
    const s = Math.max(-1, Math.min(1, f[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

function pcm16ToFloat(p: Int16Array): Float32Array<ArrayBuffer> {
  const out = new Float32Array(p.length);
  for (let i = 0; i < p.length; i++) out[i] = p[i] / 0x8000;
  return out;
}

export function bytesToBase64(bytes: Uint8Array): string {
  let bin = "";
  const step = 0x8000;
  for (let i = 0; i < bytes.length; i += step) {
    bin += String.fromCharCode(...bytes.subarray(i, i + step));
  }
  return btoa(bin);
}

export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export class MicStreamer {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private node: AudioWorkletNode | null = null;
  private pending: Float32Array[] = [];
  private pendingLen = 0;
  /** Toàn bộ giọng SV (16kHz) để gộp file đánh giá. */
  readonly recorded: Float32Array[] = [];
  private recordedLen = 0;
  private muted = false;

  constructor(private onChunk: (base64Pcm: string) => void) {}

  async start(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    this.ctx = new AudioContext();
    const url = URL.createObjectURL(new Blob([WORKLET_SRC], { type: "text/javascript" }));
    await this.ctx.audioWorklet.addModule(url);
    URL.revokeObjectURL(url);
    const src = this.ctx.createMediaStreamSource(this.stream);
    this.node = new AudioWorkletNode(this.ctx, "pcm-capture");
    const rate = this.ctx.sampleRate;
    this.node.port.onmessage = (e: MessageEvent<Float32Array>) => {
      const chunk = resample(e.data, rate, SAMPLE_RATE);
      const data = this.muted ? new Float32Array(chunk.length) : chunk;
      this.recorded.push(data);
      this.recordedLen += data.length;
      this.pending.push(data);
      this.pendingLen += data.length;
      if (this.pendingLen >= SAMPLE_RATE / 10) this.flush();
    };
    src.connect(this.node);
  }

  setMuted(muted: boolean) {
    this.muted = muted;
  }

  private flush() {
    const merged = new Float32Array(this.pendingLen);
    let off = 0;
    for (const p of this.pending) {
      merged.set(p, off);
      off += p.length;
    }
    this.pending = [];
    this.pendingLen = 0;
    const pcm = floatToPcm16(merged);
    this.onChunk(bytesToBase64(new Uint8Array(pcm.buffer)));
  }

  /** Thời lượng đã thu (giây) — đồng hồ chung của phiên. */
  get elapsed(): number {
    return this.recordedLen / SAMPLE_RATE;
  }

  async stop(): Promise<void> {
    this.node?.disconnect();
    this.stream?.getTracks().forEach((t) => t.stop());
    await this.ctx?.close().catch(() => {});
    this.node = null;
    this.ctx = null;
    this.stream = null;
  }
}

interface AiSegment {
  /** Giây tính từ đầu phiên (đồng hồ mic). */
  offset: number;
  samples: Float32Array; // 16kHz
}

export class AiPlayer {
  private ctx = new AudioContext({ sampleRate: AI_RATE });

  /** Gọi trong thao tác người dùng (chính sách autoplay của trình duyệt). */
  async resume() {
    if (this.ctx.state === "suspended") await this.ctx.resume();
  }
  private nextTime = 0;
  private sources: AudioBufferSourceNode[] = [];
  readonly segments: AiSegment[] = [];

  /** `sessionNow` = giây hiện tại của phiên (MicStreamer.elapsed). */
  play(base64Pcm: string, sessionNow: number) {
    const bytes = base64ToBytes(base64Pcm);
    const pcm = new Int16Array(bytes.buffer, bytes.byteOffset, Math.floor(bytes.length / 2));
    const f = pcm16ToFloat(pcm);
    const buf = this.ctx.createBuffer(1, f.length, AI_RATE);
    buf.copyToChannel(f, 0);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.ctx.destination);
    const startAt = Math.max(this.nextTime, this.ctx.currentTime);
    src.start(startAt);
    src.onended = () => {
      this.sources = this.sources.filter((s) => s !== src);
    };
    this.sources.push(src);
    this.segments.push({
      offset: sessionNow + (startAt - this.ctx.currentTime),
      samples: resample(f, AI_RATE, SAMPLE_RATE),
    });
    this.nextTime = startAt + buf.duration;
  }

  get speaking(): boolean {
    return this.nextTime > this.ctx.currentTime;
  }

  /** SV ngắt lời → dừng phát + cắt các đoạn AI chưa phát khỏi bản ghi. */
  interrupt(sessionNow: number) {
    this.sources.forEach((s) => {
      try {
        s.stop();
      } catch {
        /* đã dừng */
      }
    });
    this.sources = [];
    this.nextTime = this.ctx.currentTime;
    for (let i = this.segments.length - 1; i >= 0; i--) {
      const seg = this.segments[i];
      if (seg.offset >= sessionNow) {
        this.segments.splice(i, 1);
      } else {
        const keep = Math.floor((sessionNow - seg.offset) * SAMPLE_RATE);
        if (keep < seg.samples.length) seg.samples = seg.samples.subarray(0, keep);
      }
    }
  }

  async close() {
    this.interrupt(Number.POSITIVE_INFINITY);
    await this.ctx.close().catch(() => {});
  }
}

/** Trộn giọng SV (liên tục) + các đoạn AI theo mốc thời gian → WAV 16kHz mono. */
export function mixToWav(student: Float32Array[], ai: AiSegment[]): Blob {
  const studentLen = student.reduce((n, c) => n + c.length, 0);
  const aiEnd = ai.reduce(
    (m, s) => Math.max(m, Math.floor(s.offset * SAMPLE_RATE) + s.samples.length),
    0,
  );
  const total = Math.max(studentLen, aiEnd);
  const mix = new Float32Array(total);
  let off = 0;
  for (const c of student) {
    mix.set(c, off);
    off += c.length;
  }
  for (const seg of ai) {
    const start = Math.max(0, Math.floor(seg.offset * SAMPLE_RATE));
    for (let i = 0; i < seg.samples.length && start + i < total; i++) {
      mix[start + i] += seg.samples[i];
    }
  }
  const pcm = floatToPcm16(mix);
  const header = new ArrayBuffer(44);
  const v = new DataView(header);
  const writeStr = (o: number, s: string) => {
    for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i));
  };
  writeStr(0, "RIFF");
  v.setUint32(4, 36 + pcm.byteLength, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true); // PCM
  v.setUint16(22, 1, true); // mono
  v.setUint32(24, SAMPLE_RATE, true);
  v.setUint32(28, SAMPLE_RATE * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  writeStr(36, "data");
  v.setUint32(40, pcm.byteLength, true);
  return new Blob([header, new Uint8Array(pcm.buffer as ArrayBuffer)], { type: "audio/wav" });
}
